import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
	IAdminUser,
	ISyncResourcesResult,
	adminSyncResources,
	createResourceSyncConfig,
	useAdminUsersQuery,
} from '../../api/admin';

const ALL_RESOURCE_TYPES = ['watchlist', 'view', 'screener', 'worksheet'] as const;

interface Props {
	open: boolean;
	onClose: () => void;
	/** The user whose page we are on — this is the sync target */
	targetUserId: number;
}

export default function SyncResourcesDialog({ open, onClose, targetUserId }: Props) {
	const { data: allUsers, isLoading: usersLoading } = useAdminUsersQuery();

	const [sourceUser, setSourceUser] = useState<IAdminUser | null>(null);
	const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set(ALL_RESOURCE_TYPES));
	const [autoSync, setAutoSync] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [results, setResults] = useState<ISyncResourcesResult[] | null>(null);

	const eligibleSourceUsers = (allUsers ?? []).filter(
		(u) => u.is_active && u.id !== targetUserId
	);

	function toggleType(type: string) {
		setSelectedTypes((prev) => {
			const next = new Set(prev);
			if (next.has(type)) {
				next.delete(type);
			} else {
				next.add(type);
			}
			return next;
		});
	}

	function resetAndClose() {
		setSourceUser(null);
		setSelectedTypes(new Set(ALL_RESOURCE_TYPES));
		setAutoSync(false);
		setError(null);
		setResults(null);
		onClose();
	}

	async function handleSync() {
		if (!sourceUser) return;
		setSubmitting(true);
		setError(null);
		try {
			const types = Array.from(selectedTypes);

			const syncResults = await adminSyncResources(sourceUser.id, [targetUserId], types);
			setResults(syncResults);

			if (autoSync) {
				await createResourceSyncConfig(sourceUser.id, [targetUserId], types);
			}
		} catch (e: any) {
			setError(e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}

	const totalSuccess = results?.reduce((s, r) => s + r.success_count, 0) ?? 0;
	const totalSkipped = results?.reduce((s, r) => s + r.skipped_count, 0) ?? 0;
	const totalErrors = results?.reduce((s, r) => s + r.error_count, 0) ?? 0;

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{results ? (
				<>
					<DialogTitle>Sync Complete</DialogTitle>
					<DialogContent>
						<Alert severity={totalErrors > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
							{totalSuccess} synced, {totalSkipped} skipped
							{totalErrors > 0 ? `, ${totalErrors} error(s)` : ''}
							{autoSync ? ' — daily auto-sync enabled.' : '.'}
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>
							Done
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Sync Resources</DialogTitle>
					<DialogContent sx={{ pt: 2 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}

						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Copy all resources from a source user to this user.
							Previously shared resources will be updated in-place.
						</Typography>

						<Autocomplete
							options={eligibleSourceUsers}
							loading={usersLoading}
							getOptionLabel={(option) =>
								option.full_name ? `${option.full_name} (${option.email})` : option.email
							}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							value={sourceUser}
							onChange={(_, newValue) => setSourceUser(newValue)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Source user (copy from)"
									placeholder="Search by name or email..."
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{usersLoading ? <CircularProgress size={16} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							sx={{ mb: 2 }}
						/>

						<Typography variant="subtitle2" gutterBottom>
							Resource types
						</Typography>
						<FormGroup row sx={{ mb: 1 }}>
							{ALL_RESOURCE_TYPES.map((type) => (
								<FormControlLabel
									key={type}
									control={
										<Checkbox
											size="small"
											checked={selectedTypes.has(type)}
											onChange={() => toggleType(type)}
										/>
									}
									label={<Typography variant="body2">{type.charAt(0).toUpperCase() + type.slice(1)}s</Typography>}
								/>
							))}
						</FormGroup>

						<Divider sx={{ my: 2 }} />

						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={autoSync}
									onChange={(e) => setAutoSync(e.target.checked)}
								/>
							}
							label={
								<Typography variant="body2">
									Auto-sync daily
								</Typography>
							}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose} disabled={submitting}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleSync}
							disabled={submitting || !sourceUser || selectedTypes.size === 0}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Syncing...' : 'Sync'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
